import React, { memo } from 'react';
import { HeroFigureG } from '../../../common/HeroFigure';
import {
  FIGHTER_H, KIND_SCALE, MAX_GUARD, MAX_MANA, ST,
} from '../../engine/constants';
import i18n from '../../../../../i18n';

/**
 * One fighter in local coordinates: origin = feet centre, y down (SVG), facing +x for d = 1.
 * The body is the shared box-doll hero (HeroFigureG) built from `meta.look` (§11.2) — so both
 * the player and the opponent are drawn with their real assembled parts. The parent positions
 * the node (translate(x, H − y)) through `setPosRef`; this component re-renders only when the
 * discrete fields change (state, facing, grounded, hp/mana/guard, buffs, combo).
 */

/** Server state (+ airborne flag) → HeroFigure pose. */
export function poseOf(st, gr) {
  switch (st) {
    case ST.ATK_WINDUP: return 'windup';
    case ST.ATK_ACTIVE: return 'attack';
    case ST.BLOCK: return 'block';
    case ST.CAST:
    case ST.BUFF_ANIM: return 'cast';
    case ST.STUN: return 'stun';
    case ST.DEAD: return 'ko';
    case ST.WALK: return gr ? 'walk' : 'jump';
    default: return gr ? 'idle' : 'jump';
  }
}

function Bars({
  hp, maxHp, mn, gd, top, mine, enemy, compact,
}) {
  const w = compact ? 64 : 80;
  const hpPct = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
  const mnPct = Math.max(0, Math.min(1, (mn || 0) / MAX_MANA));
  const gdPct = Math.max(0, Math.min(1, (gd ?? MAX_GUARD) / MAX_GUARD));
  const hpColor = mine ? '#cfff1f' : enemy ? '#ff4d6d' : '#5ee67a';
  return (
    <g className="bf-bars" transform={`translate(${-w / 2} ${top})`}>
      <rect x="-2" y="-2" width={w + 4} height={compact ? 12 : 18} rx="4" fill="#000" opacity="0.7" />
      <rect x="0" y="0" width={w} height="7" rx="3" fill="#3a1424" />
      <rect className="bf-bars__hp" x="0" y="0" width={w * hpPct} height="7" rx="3" fill={hpColor} />
      {!compact && (
        <>
          <rect x="0" y="9" width={w} height="3.5" rx="1.5" fill="#151a3a" />
          <rect x="0" y="9" width={w * mnPct} height="3.5" rx="1.5" fill={mnPct >= 1 ? '#7ad8ff' : '#3a7bff'} className={mnPct >= 1 ? 'bf-bars__mana-full' : ''} />
        </>
      )}
      {gdPct < 1 && (
        <rect x="0" y={compact ? 8 : 13.5} width={w * gdPct} height="2" rx="1" fill={gdPct < 0.3 ? '#ff7a59' : '#ffd84a'} />
      )}
    </g>
  );
}

function Buffs({ bf, h }) {
  if (!bf || !bf.length) return null;
  const has = (b) => bf.includes(b);
  return (
    <g className="bf-buffs">
      {has('RAGE') && (
        <ellipse className="bf-buff bf-buff--rage" cx="0" cy={-h / 2} rx={h * 0.52} ry={h / 2 + 16} fill="url(#brawl-rage)" />
      )}
      {has('BERSERK') && (
        <ellipse className="bf-buff bf-buff--berserk" cx="0" cy={-h / 2} rx={h * 0.48} ry={h / 2 + 12} fill="none" stroke="#ff2d2d" strokeWidth="3" strokeDasharray="6 8" />
      )}
      {has('SHELL') && (
        <ellipse className="bf-buff bf-buff--shell" cx="0" cy={-h / 2 + 4} rx={h * 0.55} ry={h / 2 + 18} fill="rgba(94,230,122,0.16)" stroke="#5ee67a" strokeWidth="3" />
      )}
      {has('SLOW') && (
        <g className="bf-buff bf-buff--slow" stroke="#f4f4f4" strokeWidth="1.6" fill="none" opacity="0.85">
          <path d={`M-34 -16 L34 ${-h + 30} M34 -16 L-34 ${-h + 30} M0 -8 L0 ${-h + 20}`} />
          <ellipse cx="0" cy={-h / 2} rx="26" ry={h / 3} />
          <ellipse cx="0" cy={-h / 2} rx="13" ry={h / 5} />
        </g>
      )}
    </g>
  );
}

function FighterFigure({
  meta, look, st, d, gr, hp, mn, gd, cb, bfKey, dc, mine, enemy, compactBars, setPosRef, setFlashRef,
}) {
  const kind = meta?.kind || 'hero';
  const scale = KIND_SCALE[kind] || 1;
  const h = FIGHTER_H * scale;
  const bf = bfKey ? bfKey.split(',') : [];
  const pose = poseOf(st, gr);
  const berserk = bf.includes('BERSERK');
  const maxHp = Number(meta?.maxHp) || Math.max(1, hp);
  const combo = Number(cb) || 0;
  const facing = d < 0 ? -1 : 1;
  const dead = st === ST.DEAD;
  const chest = -h * 0.62;

  return (
    <g ref={setPosRef} className={`bf-pos${mine ? ' bf-pos--mine' : ''}${dc ? ' bf-pos--dc' : ''}`} data-fighter={meta?.idx}>
      <Buffs bf={bf} h={h} />
      <g ref={setFlashRef} className="bf-flashwrap">
        <g className={`bf bf--${pose} bf--combo${combo % 3}${berserk ? ' bf--berserk' : ''}${mine ? ' bf--mine' : ''}`}>
          {mine && !dead && <ellipse className="bf-aura" cx="0" cy={-h / 2} rx={h * 0.42} ry={h * 0.58} />}
          <HeroFigureG
            look={look}
            character={kind !== 'hero' ? kind : meta?.character}
            size={h}
            facing={facing}
            pose={pose}
          />
          <g transform={`scale(${facing} 1)`}>
            {/* block: energy shield in front */}
            <g className="bf-shield">
              <path d={`M${h * 0.34} ${-h * 0.95} Q${h * 0.62} ${-h * 0.5} ${h * 0.34} ${-h * 0.05}`} stroke="#7ad8ff" strokeWidth="8" fill="none" strokeLinecap="round" opacity="0.85" />
              <path d={`M${h * 0.34} ${-h * 0.95} Q${h * 0.62} ${-h * 0.5} ${h * 0.34} ${-h * 0.05}`} stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" />
            </g>
            {/* attack swoosh: reach 0..120 in front at chest height */}
            <path className="bf-swoosh" d={`M${h * 0.2} ${chest - 34} Q${h * 1.75} ${chest} ${h * 0.25} ${chest + 34}`} stroke="#fff" strokeWidth="6" fill="none" strokeLinecap="round" />
          </g>
          <circle className="bf-cast-glow" cx="0" cy={-h * 0.55} r={h * 0.62} fill="url(#brawl-cast)" />
        </g>
      </g>
      {st === ST.STUN && (
        <g className="bf-stars" transform={`translate(0 ${-h - 12})`}>
          <g className="bf-stars__spin">
            {[0, 120, 240].map((deg) => (
              <text key={deg} x={Math.cos((deg * Math.PI) / 180) * 22} y={Math.sin((deg * Math.PI) / 180) * 7} fontSize="16" textAnchor="middle">★</text>
            ))}
          </g>
        </g>
      )}
      {!dead && (
        <Bars hp={hp} maxHp={maxHp} mn={mn} gd={gd} top={-h - 30} mine={mine} enemy={enemy} compact={compactBars} />
      )}
      {mine && !dead && (
        <path className="bf-you" d={`M-8 ${-h - 44} L8 ${-h - 44} L0 ${-h - 34} Z`} fill="#cfff1f" stroke="#000" strokeWidth="2" />
      )}
      {dc ? <text className="bf-dc" y={-h - 40} textAnchor="middle">{i18n.t('brawl.offline', 'OFFLINE')}</text> : null}
    </g>
  );
}

export default memo(FighterFigure);
